/**
 * Site Data Configuration
 * Edit this file to update your portfolio content.
 * All skills, projects, experience, and section data are centralized here.
 */

// =============================================================================
// HERO SECTION
// =============================================================================
export const heroData = {
  title: "Full-Stack & AI Engineer",
  subtitle: "Building Intelligent Applications, Automation & Data-Driven Systems",
  tagline: "Software Engineering · AI Integration · Cloud & Data Platforms · Technical Product Delivery",
  summary: `I'm a New York-based Full-Stack & AI Engineer with experience building production applications, AI-powered workflows, cloud infrastructure, and data-driven solutions.

My background spans software engineering, AI integration, healthcare technology, financial services, and technical operations. I enjoy turning complex business challenges into reliable, practical technology solutions — from designing APIs and automating workflows to delivering user-focused applications.

I bring both engineering expertise and a strong understanding of business processes, cross-functional collaboration, and technical problem-solving.`,
};

// =============================================================================
// NAVIGATION
// =============================================================================
export const navItems = [
  { id: 'home', label: 'Home', type: 'scroll' },
  { id: 'expertise', label: 'Expertise', type: 'scroll' },
  { id: 'projects', label: 'Projects', type: 'scroll' },
  { id: 'articles', label: 'Articles', type: 'route', path: '/articles' },
  { id: 'opensource', label: 'Open Source', type: 'route', path: '/open-source' },
  { id: 'speaking', label: 'Speaking', type: 'route', path: '/speaking' },
  { id: 'impact', label: 'Impact', type: 'route', path: '/impact' },
  { id: 'contact', label: 'Contact', type: 'scroll' },
];

// =============================================================================
// TECHNICAL EXPERTISE (7 Categories - Tags Only, No Percentages)
// =============================================================================
export const skillCategories = [
  {
    id: 'ai-automation',
    title: 'AI & Intelligent Automation',
    skills: ['OpenAI API', 'LLM Integration', 'RAG', 'Voice AI', 'AI Agents', 'n8n', 'LangChain/LangGraph'],
  },
  {
    id: 'backend',
    title: 'Backend & API Engineering',
    skills: ['Python', 'FastAPI', 'Node.js', 'Express', 'Fastify', 'Java', 'Spring Boot', 'REST APIs', 'FHIR'],
  },
  {
    id: 'frontend',
    title: 'Frontend Development',
    skills: ['React', 'Next.js', 'TypeScript', 'JavaScript', 'Tailwind CSS', 'Responsive UI'],
  },
  {
    id: 'data-engineering',
    title: 'Data Engineering & Analytics',
    skills: ['PostgreSQL', 'MySQL', 'MongoDB', 'Azure Databricks', 'PySpark', 'ETL', 'Power BI'],
  },
  {
    id: 'cloud-devops',
    title: 'Cloud & DevOps',
    skills: ['AWS', 'Azure', 'Docker', 'CI/CD', 'Vercel', 'Netlify'],
  },
  {
    id: 'security',
    title: 'Security & Reliability',
    skills: ['JWT', 'RBAC', 'Error Handling', 'Retry Logic', 'Async Processing'],
  },
  {
    id: 'business-delivery',
    title: 'Business Systems & Delivery',
    skills: ['System Integration', 'API Troubleshooting', 'Requirements Analysis', 'Technical Documentation', 'Workflow Automation'],
  },
];

// =============================================================================
// FEATURED ENGINEERING PROJECTS
// Listed in display order. Reuses existing project data where available.
// =============================================================================
export const featuredProjectIds = [
  'healthcare-ai-voice',
  'flownav-ai',
  'nurseshift-mytabs',
  'ai-data-integrations',
];

// =============================================================================
// INDUSTRY EXPERIENCE AREAS
// =============================================================================
export const industryExperience = [
  {
    id: 'healthcare-tech',
    title: 'Healthcare Technology',
    description: 'FHIR-compliant systems, AI-powered clinical tools, voice assistants, appointment automation, and healthcare staff scheduling applications.',
    icon: 'Heart',
  },
  {
    id: 'financial-services',
    title: 'Financial Services & Analytics',
    description: 'Data pipelines, risk analytics dashboards, Power BI reporting, and business process automation for banking and insurance.',
    icon: 'TrendingUp',
  },
  {
    id: 'ai-saas',
    title: 'AI & SaaS Platforms',
    description: 'LLM integrations, intelligent workflow automation, scalable web applications, and enterprise SaaS product development.',
    icon: 'Brain',
  },
  {
    id: 'digital-products',
    title: 'Digital Products & Operations',
    description: 'High-traffic media websites, content management systems, service business applications, and technical operations.',
    icon: 'Globe',
  },
];

// =============================================================================
// SELECTED ARTICLES & RESEARCH
// TODO: Add your published articles, blog posts, or technical writing here.
// Each entry should have: id, title, publication (optional), url, date, summary
// =============================================================================
export const articles = [
  // TODO: Add your articles here. Example format:
  // {
  //   id: 'article-1',
  //   title: 'Article Title',
  //   publication: 'Publication Name (optional)',
  //   url: 'https://...',
  //   date: '2024-01-15',
  //   summary: 'Brief description of the article.',
  // },
];

// =============================================================================
// OPEN SOURCE & TECHNICAL CONTRIBUTIONS
// TODO: Add your open source projects, templates, libraries, or contributions here.
// Each entry should have: id, title, url, description, stars (optional), type
// =============================================================================
export const openSourceProjects = [
  // TODO: Add your open source contributions here. Example format:
  // {
  //   id: 'project-1',
  //   title: 'Project Name',
  //   url: 'https://github.com/...',
  //   description: 'Brief description of the project.',
  //   stars: 42, // optional
  //   type: 'library' | 'template' | 'tool' | 'contribution',
  // },
];

// =============================================================================
// SPEAKING & PROFESSIONAL COMMUNITY
// TODO: Add your talks, presentations, meetups, judging, or community involvement.
// Each entry should have: id, title, event, date, role, url (optional), description (optional)
// =============================================================================
export const speakingEngagements = [
  // TODO: Add your speaking engagements here. Example format:
  // {
  //   id: 'talk-1',
  //   title: 'Talk Title',
  //   event: 'Event or Conference Name',
  //   date: '2024-03-20',
  //   role: 'Speaker' | 'Panelist' | 'Judge' | 'Organizer' | 'Attendee',
  //   url: 'https://...', // optional - link to slides, video, or event page
  //   description: 'Brief description of the talk.', // optional
  // },
];

// =============================================================================
// PROFESSIONAL IMPACT
// TODO: Add verified achievements, recommendations, certifications, or results.
// Each entry should have: id, type, title, source (optional), description, date (optional), url (optional)
// =============================================================================
export const professionalImpact = [
  // TODO: Add your professional impact items here. Example format:
  // {
  //   id: 'impact-1',
  //   type: 'achievement' | 'recommendation' | 'certification' | 'award',
  //   title: 'Achievement or Certification Title',
  //   source: 'Source or Issuer (optional)',
  //   description: 'Brief description.',
  //   date: '2024-01-01', // optional
  //   url: 'https://...', // optional - verification link
  // },
];

// =============================================================================
// WORK EXPERIENCE (Timeline)
// =============================================================================
export const workExperience = [
  {
    period: "February 2026 – July 2026",
    title: "Full-Stack Engineer / Healthcare & Client Systems",
    company: "Contract / Independent Projects",
    description: "Designed and delivered full-stack applications for healthcare and service businesses, including K-Pediatrics & Internal Medicine and BRYZ Cleaning. Built React interfaces, Node.js/Fastify backend services, AI voice and telephony integrations, REST APIs, and automated workflows for appointments, SMS, and call routing. Improved reliability through asynchronous processing, error handling, and retries."
  },
  {
    period: "November 2025 – February 2026",
    title: "Full Stack Web Developer (AI Focus)",
    company: "Blood & Treasure / Contract",
    description: "Implement and maintain full-stack features, resolve production bugs, and optimize DataGrid performance. Contribute to AI capabilities through Python workflow automation and collaborate with stakeholders to ship AI-powered product experiences."
  },
  {
    period: "April 2025 – October 2025",
    title: "Senior Full-Stack & AI Engineer",
    company: "FlowNav AI / Contract",
    description: "Led AI-driven solution development, integrating machine learning with full-stack apps, structuring Next.js repos, aligning with compliance standards, and building onboarding and HR admin modules."
  },
  {
    period: "May 2025 – August 2025",
    title: "Data & Analytics Engineer & DevOps",
    company: "HTD Talent",
    description: "Designed automated data pipelines and Power BI dashboards using Python, PySpark, PyTorch, SQL, MongoDB, Snowflake, Azure DS, and Databricks."
  },
  {
    period: "February 2025 – May 2025",
    title: "Java Full-Stack Engineer",
    company: "HTD Talent",
    description: "Developed end-to-end systems with Java Spring Boot and React, implemented JWT authentication, integrated JDBC with MySQL, used Docker for CI/CD, and cloud technologies, including AWS, Azure, and Google Cloud."
  },
  {
    period: "August 2023 – November 2024",
    title: "Web Developer",
    company: "G/O Media",
    description: "Maintained responsive websites for major media companies, reducing errors by 60% and improving user experience for 30M+ monthly visitors, cloud infrastructure, and collaborated in Agile teams."
  },
  {
    period: "May 2023 – December 2023",
    title: "Software Engineering Peer Mentor",
    company: "Springboard / Volunteering",
    description: "Mentored aspiring engineers through project reviews, technical guidance, and coding sessions, helping them build full-stack applications."
  },
  {
    period: "September 2022 – September 2023",
    title: "Project Lead & Full-Stack Developer & Designer",
    company: "Development Center - Formats",
    description: "Designed and developed MVP products and contributed to the full software development lifecycle, serving 150+ users, also created branded products and marketing materials."
  },
  {
    period: "February 2021 – November 2022",
    title: "Full Stack Software Engineering Fellowship",
    company: "Springboard",
    description: "Built scalable web applications using JavaScript (including DOM manipulation, React, and Redux), Node.js, Express.js, HTML, CSS, designing and consuming RESTful API, Ajax, jQuery, Python, Flask, SQL, PostgreSQL, and Git/Terminal, and cloud technologies, including AWS, Azure, and Google Cloud. Implemented databases, data structures, and algorithms. Executed integration of REST APIs and third-party services (Amadeus, Weather, Spoonacular)."
  },
];

// =============================================================================
// CONTACT INFORMATION
// =============================================================================
export const contactInfo = {
  email: 'elenka_san@yahoo.com',
  location: 'New York, NY / Remote',
  github: 'https://github.com/ElenkaSan',
  linkedin: 'https://www.linkedin.com/in/elena-nurullina/',
  resume: 'https://drive.google.com/file/d/1LCkxAx4ry4kdEbxBRRA2iiWYHME0s5_L/view?usp=sharing',
};
